<template>
  <page-template :is-loading="isLoading" :data-exists="selectedServerGroupData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('serverGroup.configPage.newPageHeadline')
          : $t('serverGroup.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft>
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Server Groups List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedServerGroup && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name]) as [string | number, string][]"
          :disabled="isLoading"
          v-model:value="selectedServerGroup"
          @update:value="switchServerGroup(Array.isArray($event) ? $event[0] : $event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('serverGroup.configPage.switchTooltip')"
          classes="switch-server-group switch-page-entry"
          data-qa="switch-server-group"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight>
      <template v-if="selectedServerGroup && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateServerGroup()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadServerGroup()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteServerGroupModal()"
          class="delete-button"
          :disabled="isLoading || selectedServerGroupProtected"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isServerGroupValid"
        :tooltip="saveToolTip"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('serverGroup.configPage.name')"
              :placeholder="$t('serverGroup.configPage.name')"
              v-model:inputValue="selectedServerGroupData.name"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="challenge-cookie-domain"
              classes="challenge-cookie-domain"
              data-qa="challenge-cookie-domain-input"
              :label="$t('serverGroup.configPage.challengeCookieDomain')"
              :placeholder="$t('serverGroup.configPage.challengeCookieDomain')"
              v-model:inputValue="selectedServerGroupData['challenge_cookie_domain']"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('serverGroup.configPage.description')"
              v-model:inputValue="selectedServerGroupData.description"
              rows="2"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="match"
              classes="match"
              data-qa="match-textarea"
              :label="$t('serverGroup.configPage.match')"
              :placeholder="$t('serverGroup.configPage.matchPlaceholder')"
              v-model:inputValue="serverNames"
              rows="6"
            />
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              :items="mapServerCertificates as [string | number, string][]"
              v-model:value="selectedServerGroupData['ssl_certificate']"
              :label="$t('serverGroup.configPage.serverCertificate')"
              class="server-certificate-dropdown"
              data-qa="server-certificate-dropdown"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-button
              :label="$t('serverGroup.configPage.generateCertificate')"
              variant="secondary"
              @button-click="generateCertificate()"
              :is-loading="isCertificateGenerateLoading"
              class="generate-certificate-button"
              :disabled="isCertificateGenerateLoading || isNew || entityNotSaved"
              :tooltip="
                isNew || entityNotSaved
                  ? $t('serverGroup.configPage.generateCertificateNotSavedTooltip')
                  : ''
              "
              data-qa="generate-certificate-button"
            />
          </div>
          <div class="client-ca-certificate-config" v-if="isClientCaEnabled">
            <div class="rbz-field">
              <label class="rbz-label">
                {{ $t('serverGroup.configPage.clientCaCertificate.title') }}
              </label>
            </div>
            <div class="rbz-field">
              <rbz-dropdown
                :items="mapClientCaCertificate as [string | number, string][]"
                v-model:value="selectedServerGroupData['client_certificate']"
                :label="$t('serverGroup.configPage.clientCaCertificate.label')"
                class="client-ca-certificate-dropdown"
                data-qa="client-ca-certificate-dropdown"
                type="single-select"
                :can-clear-single-select="true"
              />
            </div>
            <div class="rbz-field">
              <rbz-dropdown
                :items="clientCaModeItems as [string | number, string][]"
                :value="selectedServerGroupData['client_certificate_mode']"
                @update:value="
                  (v: string | string[]) =>
                    clientCaModeValue((Array.isArray(v) ? v[0] : v) as ClientCertificateMode)
                "
                :placeholder="$t('serverGroup.configPage.clientCaMode.placeholder')"
                :label="$t('serverGroup.configPage.clientCaMode.label')"
                class="client-ca-certificate-mode-dropdown"
                data-qa="client-ca-certificate-mode-dropdown"
              />
            </div>
            <div class="rbz-field rbz-field-toggle">
              <rbz-form-toggle
                v-model:buttonValue="selectedServerGroupData['crl_verify']"
                :label="$t('serverGroup.configPage.clientCaCrl.toggleLabel')"
                class="crl-verify-toggle"
                data-qa="crl-verify-toggle"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="rbz-columns">
        <div class="rbz-column is-8" v-if="selectedServerGroupData['crl_verify']">
          <div class="rbz-field">
            <CrlRevocationList
              :crlList="crlTableData"
              @update:crlList="handleCrlListUpdate($event)"
              @validate:crlList="validateCrlList($event)"
            />
          </div>
        </div>
      </div>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              :items="
                securityPolicies.map((item) => [item.id, item.name]) as [string | number, string][]
              "
              v-model:value="selectedServerGroupData['security_policy']"
              :label="$t('serverGroup.securityPolicy')"
              classes="security-policy-dropdown"
              data-qa="security-policy-dropdown"
            />
          </div>
        </div>
      </div>
      <div class="rbz-card white" v-if="selectedSecurityPolicyData?.map">
        <rbz-table
          :columns="securityPolicyPathMappingColumns"
          :table-data="selectedSecurityPolicyData.map"
          rows-per-page="20"
          class="security-policy-path-mapping-table"
          data-qa="path-mapping-table"
          :show-top-navbar="false"
          :show-buttons-column="false"
        >
        </rbz-table>
      </div>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              :items="
                proxyTemplates.map((item) => [item.id, item.name]) as [string | number, string][]
              "
              v-model:value="selectedServerGroupData['proxy_template']"
              :label="$t('serverGroup.proxyTemplate')"
              classes="proxy-template-dropdown"
              data-qa="proxy-template-dropdown"
            />
          </div>
        </div>
      </div>
      <div class="rbz-columns" v-if="mobileApplicationGroups.length">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              :items="
                mobileApplicationGroups.map((item) => [item.id, item.name]) as [
                  string | number,
                  string,
                ][]
              "
              v-model:value="selectedServerGroupData['mobile_application_group']"
              :label="$t('serverGroup.mobileApplicationGroup')"
              classes="mobile-application-group-dropdown"
              data-qa="mobile-application-group-dropdown"
              type="single-select"
              :can-clear-single-select="true"
            />
          </div>
        </div>
      </div>
      <rbz-modal-dialog
        :show="showDeleteServerGroupModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        :primary-btn-disabled="!isDeleteServerGroupNameValid"
        @onPrimaryBtnClick="deleteServerGroup"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteServerGroupModal = false"
        @closeModal="showDeleteServerGroupModal = false"
        size="medium"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="delete-server-group-name-input"
              data-qa="delete-server-group-name-input"
              :label="$t('serverGroup.deleteConfirm')"
              :placeholder="$t('serverGroup.deleteConfirmPlaceholder')"
              v-model:inputValue="deleteServerGroupName"
            />
          </div>
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  AclProfile,
  BackendService,
  BasicDocument,
  Certificate,
  ContentFilterProfile,
  DocumentName,
  MobileApplicationGroup,
  ProxyTemplate,
  SecurityPolicy,
  ServerGroup,
  ClientCertificateMode,
  CrlRevocationListData,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { GenericObject, RbzTableColumnOptions } from 'ui-component-library/rbz/types';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import { useRouting } from '@/helpers/RoutingUtils';
import CrlRevocationList from '@/components/CrlRevocationList.vue';
import { AxiosError } from 'axios';

export default defineComponent({
  name: 'ServerGroupsEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    RbzFormButton,
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    RbzTable,
    CrlRevocationList,
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedServerGroup: null as string,
      selectedServerGroupData: null as ServerGroup,
      originalServerGroupData: null as ServerGroup,
      pageLoadingCounter: null as number | null,
      isDeleteLoading: false,
      showDeleteServerGroupModal: false,
      deleteServerGroupName: '',

      certificates: [] as Certificate[],
      isCertificateGenerateLoading: false,
      securityPolicies: [] as SecurityPolicy[],
      proxyTemplates: [] as ProxyTemplate[],
      mobileApplicationGroups: [] as MobileApplicationGroup[],
      backendServices: [] as BackendService[],
      contentFilterProfiles: [] as ContentFilterProfile[],
      aclProfiles: [] as AclProfile[],
      clientCaModeItems: [
        ['on', this.$t('serverGroup.configPage.clientCaMode.on')],
        ['off', this.$t('serverGroup.configPage.clientCaMode.off')],
        ['optional', this.$t('serverGroup.configPage.clientCaMode.optional')],
      ],
      validNlb: false,
      isClientToV5ClientCaCertEnabled: false,

      securityPolicyPathMappingColumns: [
        {
          id: 'name',
          title: this.$t('serverGroup.configPage.securityPolicyTable.name'),
          fieldNames: ['name'],
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'match',
          title: this.$t('serverGroup.configPage.securityPolicyTable.matchPath'),
          fieldNames: ['match'],
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return item.id === '__site_level__' ? 'hidden' : '';
          },
        },
        {
          id: 'content-filter-profile',
          title: this.$t('serverGroup.configPage.securityPolicyTable.contentFilterProfile'),
          fieldNames: ['content_filter_profile'],
          displayFunction: (item) => {
            const profile = _.find(this.contentFilterProfiles, (profile) => {
              return profile.id === item['content_filter_profile'];
            });
            return profile?.name || '';
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return `${item['content_filter_profile_active'] ? 'rbz-text-success' : 'rbz-text-danger'}
           ${item.id === '__site_level__' ? ' hidden' : ''}`;
          },
        },
        {
          id: 'acl-profile',
          title: this.$t('serverGroup.configPage.securityPolicyTable.aclProfile'),
          fieldNames: ['acl_profile_active'],
          displayFunction: (item) => {
            const profile = _.find(this.aclProfiles, (profile) => {
              return profile.id === item['acl_profile'];
            });
            return profile?.name || '';
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return `${item['acl_profile_active'] ? 'rbz-text-success' : 'rbz-text-danger'}
           ${item.id === '__site_level__' ? ' hidden' : ''}`;
          },
        },
        {
          id: 'backend-service',
          title: this.$t('serverGroup.configPage.securityPolicyTable.backendService'),
          fieldNames: ['backend_service'],
          displayFunction: (item) => {
            return this.backendServiceName(item['backend_service']);
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return item.id === '__site_level__' ? 'hidden' : '';
          },
        },
        {
          id: 'rate-limit-rules',
          title: this.$t('serverGroup.configPage.securityPolicyTable.rateLimitRules'),
          fieldNames: ['rate_limit_rules'],
          displayFunction: (item) => {
            return item['rate_limit_rules']?.length || 0;
          },
          classes: 'width-120px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'edge-functions',
          title: this.$t('serverGroup.configPage.securityPolicyTable.edgeFunctions'),
          fieldNames: ['edge_functions'],
          displayFunction: (item) => {
            return item['edge_functions']?.length || 0;
          },
          classes: 'width-120px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
    };
  },
  async beforeMount() {
    const url = 'tools/versions';
    const response = await RequestsUtils.sendRequest({
      methodName: 'GET',
      url,
    });
    if (response?.data) {
      this.validNlb = response.data.nlb;
    }
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('serverGroup.plural'),
        singular: this.$t('serverGroup.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    selectedServerGroupProtected(): boolean {
      return this.selectedServerGroup?.startsWith('__');
    },

    deleteTooltip(): string {
      if (this.selectedServerGroupProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      return '';
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedServerGroupData, this.originalServerGroupData);
    },

    selectedServerGroupName(): string {
      return (
        _.find(this.dropdownData, (dropdownItem) => {
          return dropdownItem.id === this.selectedServerGroup;
        })?.name || ''
      );
    },

    isDeleteServerGroupNameValid(): boolean {
      const inputName = this.deleteServerGroupName.trim();
      return inputName === this.selectedServerGroupName;
    },

    serverNames: {
      get(): string {
        return Array.from(this.selectedServerGroupData?.['server_names'] || '').join('\n');
      },
      set(newValue: string) {
        const value = newValue.replaceAll(' ', '');
        this.selectedServerGroupData['server_names'] = value.split('\n');
      },
    },

    crlTableData(): CrlRevocationListData[] {
      const crlList = (this.selectedServerGroupData?.crl_statuses ?? []) as CrlRevocationListData[];
      return crlList.map((crl) => ({
        id: crl.id ?? DatasetsUtils.generateUUID2(),
        url: crl.url,
        issuer: crl.issuer,
        status: crl.status,
        last_update: crl.last_update,
        last_download: crl.last_download,
        error: crl.error,
      }));
    },

    selectedSecurityPolicyData(): SecurityPolicy {
      return this.securityPolicies?.find((securityPolicy) => {
        return securityPolicy.id === this.selectedServerGroupData['security_policy'];
      });
    },

    isServerGroupValid(): boolean {
      const trimmedName = this.selectedServerGroupData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      if (this.isClientCaEnabled && this.isInvalidV5ClientCertConfig()) {
        return false;
      }
      if (this.selectedServerGroupData?.crl_verify && !this.isCertificateRevocationListValid()) {
        return false;
      }
      if (this.selectedServerGroupData?.crl_verify && !this.isCrlListHasUniqueUrls()) {
        return false;
      }
      const nameValid =
        this.selectedServerGroupData.name &&
        (trimmedName === this.originalServerGroupData.name.toLowerCase().trim() || !nameExists);
      return nameValid;
    },

    mapServerCertificates(): [string, string][] {
      const certificates = _.filter(this.certificates, (certificate: Certificate) => {
        return certificate.side === 'server';
      });
      return certificates.map((item) => [item.id, item.name]);
    },

    mapClientCaCertificate(): [string, string][] {
      let certificates = _.filter(this.certificates, (certificate: Certificate) => {
        return certificate.side === 'clientCA';
      });
      return certificates.map((item) => [item.id, item.name]);
    },

    isClientCaEnabled(): boolean {
      return this.validNlb && this.isClientToV5ClientCaCertEnabled;
    },

    saveToolTip() {
      if (this.isClientCaEnabled && this.isInvalidV5ClientCertConfig()) {
        return this.$t('serverGroup.configPage.clientCaMode.errorTooltip');
      }
      if (this.selectedServerGroupData?.crl_verify && !this.isCertificateRevocationListValid()) {
        return this.$t('serverGroup.configPage.clientCaCrl.errorTooltip');
      }
      if (this.selectedServerGroupData?.crl_verify && !this.isCrlListHasUniqueUrls()) {
        return this.$t('serverGroup.configPage.clientCaCrl.duplicateUrlsErrorTooltip');
      }
      return '';
    },
  },
  methods: {
    handleCrlListUpdate(crlList: CrlRevocationListData[]) {
      this.selectedServerGroupData.cdp = crlList.map((crl) => crl.url);
      this.selectedServerGroupData.crl_statuses = crlList;
      // check the cdp, and remove the entry if the entry is removed from crl_statusus.
    },

    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('server-groups'));
    },

    switchServerGroup(serverGroupId: string) {
      this.navigateTo(this.getEditPath('server-groups', serverGroupId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newServerGroup(): ServerGroup {
      const factory = DatasetsUtils.newEntityFactory['server-groups'];
      return factory && factory();
    },

    async loadServerGroup(serverGroupId: string) {
      this.setPageLoadingStatus(true);
      this.selectedServerGroup = serverGroupId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups/${serverGroupId}`,
      });
      this.selectedServerGroupData = response?.data || {};
      this.originalServerGroupData = _.cloneDeep(this.selectedServerGroupData);
      this.setPageLoadingStatus(false);
    },

    async duplicateServerGroup() {
      this.setPageLoadingStatus(true);
      const serverGroupToAdd = _.cloneDeep(this.selectedServerGroupData);
      serverGroupToAdd.id = DatasetsUtils.generateUUID2();
      serverGroupToAdd.name = Utils.generateUniqueEntityName(
        serverGroupToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/server-groups/${serverGroupToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: serverGroupToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchServerGroup(serverGroupToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadServerGroup() {
      Utils.downloadFile('server-group', 'json', this.selectedServerGroupData);
    },

    openDeleteServerGroupModal() {
      this.showDeleteServerGroupModal = true;
      this.deleteServerGroupName = '';
    },

    async deleteServerGroup() {
      this.isDeleteLoading = true;
      const serverGroupId = this.selectedServerGroup;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === serverGroupId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedServerGroup = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/server-groups/${serverGroupId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    normalizeCrlList(crlList: CrlRevocationListData[]): CrlRevocationListData[] {
      return (crlList ?? []).map((crl) => {
        return {
          url: crl.url,
          issuer: crl.issuer,
          last_update: crl.last_update,
          last_download: crl.last_download,
          status: crl.status,
          error: crl.error,
        };
      });
    },

    getCrlValidationError(error: AxiosError): Record<string, unknown> | null {
      const responseData = error?.response?.data as GenericObject | undefined;
      return responseData?.data ?? null;
    },

    async validateCrlList(crlList: CrlRevocationListData[]) {
      if (!this.selectedServerGroupData) return;
      if (this.isNew || !this.selectedServerGroupData.id) {
        return;
      }
      this.setPageLoadingStatus(true);
      const normalizedCrl = crlList.map((item) => item.url);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url: 'verify-certificate-revocation-list',
        data: normalizedCrl,
        displayServerFailureMessage: true,
        onFail: (error) => {
          const crlValidationError = this.getCrlValidationError(error);
          if (crlValidationError && crlValidationError.crl_statuses) {
            this.selectedServerGroupData.crl_statuses = _.cloneDeep(
              crlValidationError.crl_statuses as CrlRevocationListData[]
            );
          }
        },
      });
      if (response?.data?.data?.crl_statuses) {
        this.selectedServerGroupData.crl_statuses = _.cloneDeep(
          response.data.data.crl_statuses as CrlRevocationListData[]
        );
      }
      this.setPageLoadingStatus(false);
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedServerGroupData.name = this.selectedServerGroupData.name.trim();
      let serverNames = this.selectedServerGroupData['server_names'];
      serverNames = _.filter(serverNames, (serverName: string) => {
        return serverName?.length > 0;
      });
      this.selectedServerGroupData.crl_statuses = this.normalizeCrlList(
        this.selectedServerGroupData.crl_statuses
      );

      this.selectedServerGroupData['server_names'] = serverNames;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/server-groups/${this.selectedServerGroupData.id}`,
        data: this.selectedServerGroupData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedServerGroupData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          if (response?.data?.data?.crl_statuses) {
            this.selectedServerGroupData.crl_statuses = _.cloneDeep(
              response.data.data.crl_statuses as CrlRevocationListData[]
            );
          }
          this.originalServerGroupData = _.cloneDeep(this.selectedServerGroupData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadCertificates() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/certificates`,
        config: { headers: { 'x-fields': 'id, san' } },
      });
      this.certificates = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadSecurityPolicies() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      this.securityPolicies = response?.data?.items || [];
    },

    async loadProxyTemplates() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/proxy-templates`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.proxyTemplates = response?.data?.items || [];
    },

    async loadMobileApplicationGroups() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/mobile-application-groups`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.mobileApplicationGroups = response?.data?.items || [];
    },

    async loadBackendServices() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/backend-services`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.backendServices = response?.data?.items || [];
    },

    async loadContentFilterProfiles() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-profiles`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.contentFilterProfiles = response?.data?.items || [];
    },

    async loadAclProfiles() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/acl-profiles`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.aclProfiles = response?.data?.items || [];
    },

    isInvalidV5ClientCertConfig(): boolean {
      return (
        this.selectedServerGroupData['client_certificate'] === '' &&
        this.selectedServerGroupData['client_certificate_mode'] !== 'off'
      );
    },

    async loadNamespace() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `db/system`,
      });
      const featureToggle = response?.data?.['feature-toggle'];
      if (featureToggle) {
        this.isClientToV5ClientCaCertEnabled =
          !!featureToggle['ssl-client-to-v5-client-ca-certificate'];
      }
    },

    async generateCertificate() {
      this.isCertificateGenerateLoading = true;
      const method = 'POST';
      let queryParameters = '';
      const serverNames: string[] = _.filter(
        this.selectedServerGroupData['server_names'],
        (serverName: string) => {
          return serverName?.length > 0;
        }
      );
      _.forEach(serverNames, (serverName: string) => {
        if (queryParameters.length) {
          queryParameters += '&';
        }
        queryParameters += `domains=${encodeURIComponent(serverName)}`;
      });
      const serverGroupId = encodeURIComponent(this.selectedServerGroupData.id);
      queryParameters += `&site-id=${serverGroupId}`;
      const successMessage = this.$t('serverGroup.configPage.generateCertificateSuccess');
      const failureMessage = this.$t('serverGroup.configPage.generateCertificateFailure');
      const newId = DatasetsUtils.generateUUID2();
      const url = `conf/${this.selectedBranch}/certificates/${newId}?${queryParameters}`;
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: method,
        url: url,
        data: { id: newId },
        successMessage,
        failureMessage,
      });
      await this.loadCertificates();
      this.isCertificateGenerateLoading = false;
    },

    backendServiceName(id: string): string {
      const backendService = _.find(this.backendServices, (backendService) => {
        return backendService.id === id;
      });
      return backendService?.name || '';
    },

    clientCaModeValue(new_client_certificate_mode: ClientCertificateMode) {
      this.selectedServerGroupData['client_certificate_mode'] = new_client_certificate_mode;
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedServerGroupData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params as [ServerGroup, boolean];
          Utils.saveSessionData(formData as GenericObject, Boolean(entityNotSaved));
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },

    isCertificateRevocationListValid() {
      const crlUrlList = this.selectedServerGroupData?.crl_statuses;
      if (!crlUrlList) {
        return true;
      }
      return _.every(crlUrlList, (crl: CrlRevocationListData) => {
        return crl.status === true;
      });
    },

    isCrlListHasUniqueUrls(): boolean {
      const crlList = this.selectedServerGroupData?.crl_statuses ?? [];
      return _.uniqBy(crlList, 'url').length === crlList.length;
    },
  },
  created() {
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Server Group', 'Server Groups Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedServerGroupData = this.newServerGroup();
            this.selectedServerGroupData.name = Utils.generateUniqueEntityName(
              this.selectedServerGroupData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalServerGroupData = _.cloneDeep(this.selectedServerGroupData);
          } else {
            const serverGroupId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === serverGroupId;
            });
            if (dataExists) {
              await this.loadServerGroup(serverGroupId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedServerGroupData = JSON.parse(savedSessionData);
          }
          const certificatesPromise = this.loadCertificates();
          const securityPoliciesPromise = this.loadSecurityPolicies();
          const proxyTemplatesPromise = this.loadProxyTemplates();
          const mobileApplicationGroupsPromise = this.loadMobileApplicationGroups();
          const backendServicesPromise = this.loadBackendServices();
          const contentFilterProfilesPromise = this.loadContentFilterProfiles();
          const aclProfilesPromise = this.loadAclProfiles();
          const namespacePromise = this.loadNamespace();
          await Promise.all([
            certificatesPromise,
            securityPoliciesPromise,
            proxyTemplatesPromise,
            mobileApplicationGroupsPromise,
            backendServicesPromise,
            contentFilterProfilesPromise,
            aclProfilesPromise,
            namespacePromise,
          ]);
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>

<style scoped lang="scss">
:deep(.rbz-table) {
  .data-cell .hidden {
    display: none;
  }
}

.client-ca-certificate-mode-dropdown {
  :deep(.dropdown-header) {
    .selected-value {
      width: 85%;
    }
  }
}
</style>
